import React from 'react';
import SectionHeader from './SectionHeader';
import useInView from '../hooks/useInView';

const BIRTH_YEAR = 1998;
const BIRTH_MONTH = 2;
const BIRTH_DAY = 15;

function calculateAge(referenceDate = new Date()) {
  const year = referenceDate.getFullYear();
  const month = referenceDate.getMonth() + 1;
  const day = referenceDate.getDate();

  let age = year - BIRTH_YEAR;
  if (month < BIRTH_MONTH || (month === BIRTH_MONTH && day < BIRTH_DAY)) {
    age -= 1;
  }

  return age;
}

function resolveImagePath(imagePath) {
  if (!imagePath) {
    return '';
  }

  const normalizedPath = imagePath.replace(/^\/+/, '');
  return `${process.env.PUBLIC_URL}/${normalizedPath}`;
}

function InstagramIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <rect x="1.6" y="1.6" width="12.8" height="12.8" rx="3.4" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="8" cy="8" r="3" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="11.7" cy="4.3" r="0.9" fill="currentColor" />
    </svg>
  );
}

function GitHubIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M8 0a8 8 0 0 0-2.53 15.59c.4.075.546-.173.546-.385l-.01-1.34c-2.22.482-2.69-1.07-2.69-1.07-.364-.924-.89-1.17-.89-1.17-.727-.497.055-.487.055-.487.803.057 1.226.824 1.226.824.714 1.223 1.872.87 2.328.665.072-.517.279-.87.508-1.07-1.773-.202-3.637-.887-3.637-3.95 0-.873.312-1.587.823-2.146-.083-.202-.357-1.016.078-2.12 0 0 .67-.215 2.2.82a7.62 7.62 0 0 1 4 0c1.53-1.035 2.198-.82 2.198-.82.436 1.104.162 1.918.08 2.12.513.559.822 1.273.822 2.146 0 3.07-1.867 3.746-3.647 3.944.287.248.543.736.543 1.484l-.01 2.2c0 .214.145.464.55.384A8 8 0 0 0 8 0z"
      />
    </svg>
  );
}

function AboutSection({ language, mode }) {
  const [bodyRef, isVisible] = useInView();
  const age = calculateAge();
  const isTraveler = mode === 'traveler';
  const profileImage = resolveImagePath(
    isTraveler ? 'images/travels/nz-cycling/10.jpg' : 'images/profile_pictures/cybozu.jpg'
  );

  const copy =
    language === 'ja'
      ? {
          label: 'ABOUT',
          title: '自己紹介',
          nameMain: '𠮷田 起弥',
          nameSub: 'よしだ たつや',
          handle: '@yoshidon',
          intro: `奈良県生駒郡斑鳩町出身のソフトウェア開発者。1998年2月15日生（${age}歳）`,
          paragraphs: isTraveler
            ? [
                'これまでに34カ国を訪問。ゴールを決めて旅に出て、道中の立ち止まりも寄り道も大歓迎——そんな冒険のような旅が好きです。',
                '原点は、小さい頃に夢中になったポケモン。ゲームとアニメの世界に憧れて、「10歳になったら旅に出るんだ」と本気で思っていました。',
                '中3の夏、友達2人と自転車にテントを積んで四国を一周。何時間も漕いでようやく次の街が見えてくるあの感覚がまさにポケモンの世界で、以来、旅の魅力に取り憑かれています。',
              ]
            : [
                '東京大学で金融・経営学を学んだのち、サイボウズで約150社の業務改善、システム導入提案に従事。',
                'その後システム開発の魅力に惹かれ、カナダ・ブリティッシュコロンビア工科大学での2年間の修行を経て、フリーランスエンジニアとしてのキャリアをスタート。',
                '現在は yamacomi の開発に携わり、フロントエンド、バックエンドからインフラ、保守、運用までを任されています。',
              ],
          credentialsLabel: isTraveler ? "TRAVELER'S LOG" : 'CREDENTIALS',
          credentials: isTraveler
            ? ['訪問国数：34カ国', '原点：中3の夏、自転車での四国一周']
            : ['応用情報技術者', 'kintone認定 アプリデザインスペシャリスト'],
          imageAlt: isTraveler
            ? 'ニュージーランド南島の最南端ブラフで、道標と自転車とともに'
            : 'サイボウズ時代のプロフィール写真',
          links: {
            instagram: 'Instagramプロフィールを開く',
            github: 'GitHubプロフィールを開く',
          },
        }
      : {
          label: 'ABOUT',
          title: 'About',
          nameMain: 'Tatsuya Yoshida',
          nameSub: '𠮷田 起弥',
          handle: '@yoshidon',
          intro: `Software developer from Ikaruga, Nara, Japan. Born on Feb 15, 1998 (age ${age}).`,
          paragraphs: isTraveler
            ? [
                '34 countries so far. My favorite kind of journey is an adventure with a goal: set a destination, then welcome every detour and every stop along the way.',
                'It started with Pokémon. I grew up on the games and the anime, genuinely believing I would set off on a journey at age ten.',
                'At fourteen, two friends and I circled Shikoku by bicycle with tents on our racks. Pedaling for hours before the next town appeared felt exactly like that world — and I have been hooked on traveling ever since.',
              ]
            : [
                'After studying finance and management at the University of Tokyo, I worked at Cybozu and supported business improvement and system implementation proposals for around 150 companies.',
                'Then, drawn by the appeal of software development, I trained for two years at BCIT in Canada and started my career as a freelance engineer.',
                'I am currently involved in developing yamacomi, covering frontend, backend, infrastructure, maintenance, and operations.',
              ],
          credentialsLabel: isTraveler ? "TRAVELER'S LOG" : 'CREDENTIALS',
          credentials: isTraveler
            ? ['34 countries visited', 'Origin: around Shikoku by bicycle, age 14']
            : [
                'Applied Information Technology Engineer',
                'kintone Certified App Design Specialist',
              ],
          imageAlt: isTraveler
            ? 'With my bike and the signpost at Bluff, the southern tip of New Zealand\'s South Island'
            : 'Profile photo during the Cybozu period',
          links: {
            instagram: 'Open Instagram profile',
            github: 'Open GitHub profile',
          },
        };

  return (
    <section id="about" className="about-section" aria-label={copy.title}>
      <SectionHeader index="02" label={copy.label} title={copy.title} />

      <div ref={bodyRef} className={`about-body reveal ${isVisible ? 'is-visible' : ''}`}>
        <figure className="about-portrait">
          <img src={profileImage} alt={copy.imageAlt} loading="lazy" />
          <figcaption className="mono">{copy.handle}</figcaption>
        </figure>

        <div className="about-text">
          <p className="about-name">
            <span className="about-name__main">{copy.nameMain}</span>
            <span className="about-name__sub mono">{copy.nameSub}</span>
          </p>
          <p className="about-intro">{copy.intro}</p>

          {copy.paragraphs.map((paragraph, index) => (
            <p key={`about-paragraph-${index}`} className="about-paragraph">
              {paragraph}
            </p>
          ))}

          <div className="about-credentials">
            <p className="about-credentials__label mono">{copy.credentialsLabel}</p>
            <ul className="about-credentials__list">
              {copy.credentials.map((credential) => (
                <li key={credential}>{credential}</li>
              ))}
            </ul>
          </div>

          <nav className="about-socials" aria-label={language === 'ja' ? 'SNSリンク' : 'Social links'}>
            <a
              href="https://www.instagram.com/yoshidont_mind/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label={copy.links.instagram}
            >
              <InstagramIcon />
              <span className="mono">Instagram</span>
            </a>
            <a
              href="https://github.com/yoshidont-mind"
              target="_blank"
              rel="noopener noreferrer"
              aria-label={copy.links.github}
            >
              <GitHubIcon />
              <span className="mono">GitHub</span>
            </a>
          </nav>
        </div>
      </div>
    </section>
  );
}

export default AboutSection;
